import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { fetchAvailableDates, fetchCustomShocks, fetchPortfolioGreeks } from "../../../services/RiskMonitorAxios";
import GVarLoader from "../../ui/GVarLoader";
import StrategyBreakdownModal from "./StrategyBreakdownModal";
import { ScenarioTable } from "./StressScenarioPanel";

const STRATEGY_ORDER = [
  "MERGER ARBITRAGE",
  "SPAC LIQUIDATION ARBITRAGE",
  "EVENT DRIVEN FIXED INCOME",
  "CONVERTIBLE ARBITRAGE",
  "CREDIT ARBITRAGE",
  "STAT ARBITRAGE",
  "VOL ARBITRAGE",
];

// Stable empty default so ScenarioTable's memo doesn't re-run every render.
const NO_GROUPS = [];

const GREEKS_GROUP_KEY = "GREEKS";
const GREEKS_GROUP = { key: GREEKS_GROUP_KEY, label: "Greeks" };

// A 404 just means no file exists for that dataset/date — treat it as an
// empty result for that dataset rather than failing the whole page.
function settleScenarioFetch(result) {
  if (result.status === "fulfilled") return { payload: result.value, error: null };
  if (result.reason?.response?.status === 404) {
    return { payload: { scenarios: [], extra_columns: [] }, error: null };
  }
  return { payload: null, error: result.reason };
}

export default function CustomShocksPage() {
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drillDown, setDrillDown] = useState(null);
  // Keyed by the backend's group keys; a missing key means expanded.
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showBpsOfNav, setShowBpsOfNav] = useState(false);

  const strategyColumns = useMemo(() => {
    if (!data?.extra_columns) return [];
    return (data.extra_columns || []).filter((col) => {
      const colUpper = String(col).toUpperCase();
      return STRATEGY_ORDER.some((strategy) => colUpper.includes(strategy.toUpperCase()));
    });
  }, [data?.extra_columns]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dates = await fetchAvailableDates();
        if (cancelled) return;
        const sorted = [...dates].sort().reverse();
        setAvailableDates(sorted);
        setSelectedDate((current) => current || sorted[0] || "");
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load dates");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedDate) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    Promise.allSettled([
      fetchCustomShocks(selectedDate, portfolio || null, { signal: controller.signal }),
      fetchPortfolioGreeks(selectedDate, portfolio || null, { signal: controller.signal }),
    ])
      .then(([customResult, greeksResult]) => {
        if (controller.signal.aborted) return;
        const custom = settleScenarioFetch(customResult);
        const greeks = settleScenarioFetch(greeksResult);

        if (custom.error && greeks.error) {
          setError(
            custom.error?.response?.data?.detail
            || custom.error?.message
            || "Failed to load custom shocks and portfolio greeks",
          );
        }

        const customScenarios = custom.payload?.scenarios || [];
        // Greeks scenarios (Beta, Vega, DV01, CS01, …) are collapsed into a
        // single flat "Greeks" group — any thematic grouping (US Equity,
        // Volatility, Rates, Credit) the backend tags them with is dropped.
        const greeksScenarios = (greeks.payload?.scenarios || []).map((s) => ({
          ...s,
          group: GREEKS_GROUP_KEY,
        }));

        const mergedGroups = greeksScenarios.length > 0
          ? [GREEKS_GROUP, ...(custom.payload?.scenario_groups || [])]
          : (custom.payload?.scenario_groups || []);

        const extraColumns = Array.from(new Set([
          ...(custom.payload?.extra_columns || []),
          ...(greeks.payload?.extra_columns || []),
        ]));

        setData({
          scenarios: [...customScenarios, ...greeksScenarios],
          scenario_groups: mergedGroups,
          extra_columns: extraColumns,
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [selectedDate, portfolio]);

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2.5 }, width: "100%", bgcolor: "#f8f9fa" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 16 }} /> Date
            </Box>
          </InputLabel>
          <Select value={selectedDate} label="Date" onChange={(e) => setSelectedDate(e.target.value)}>
            {availableDates.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Portfolio</InputLabel>
          <Select value={portfolio} label="Portfolio" onChange={(e) => setPortfolio(e.target.value)}>
            <MenuItem value="">All (Firmwide)</MenuItem>
            <MenuItem value="GCEMF">GCEMF</MenuItem>
            <MenuItem value="GCM">GCM</MenuItem>
            <MenuItem value="GIP">GIP</MenuItem>
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
          Rows and columns follow the custom shock and portfolio greeks files for this date.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showBpsOfNav}
              onChange={(e) => setShowBpsOfNav(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#006842" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#006842" },
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
              bps of NAV
            </Typography>
          }
          sx={{ ml: "auto", mr: 0 }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <GVarLoader />}
      {!loading && (
        <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: { xs: 1, sm: 2 }, bgcolor: "background.paper" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "#BE9B53", mb: 1 }}>
            Custom Shocks
          </Typography>
          <ScenarioTable
            rows={data?.scenarios || []}
            extraColumns={strategyColumns}
            expanded
            showBpsOfNav={showBpsOfNav}
            groupScenarios
            scenarioGroups={data?.scenario_groups || NO_GROUPS}
            expandedGroups={expandedGroups}
            onToggleGroup={(groupKey) => {
              setExpandedGroups((prev) => ({
                ...prev,
                [groupKey]: !(prev[groupKey] ?? true),
              }));
            }}
            // Greeks' unshocked base case always sits at the top, above every group.
            pinRow={(row) => row.group === GREEKS_GROUP_KEY && row.sim_number === 0}
            sortConfig={sortConfig}
            onSort={(key) => {
              setSortConfig((prev) => {
                if (prev.key === key) {
                  return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                  };
                }
                return { key, direction: "asc" };
              });
            }}
            onSimClick={(simNumber, pnl, scenarioLabel, row) => {
              setDrillDown({
                date: selectedDate,
                simNumber,
                pnl,
                scenarioLabel,
                portfolio: portfolio || null,
                source: row?.group === GREEKS_GROUP_KEY ? "greeks" : "simulation",
              });
            }}
          />
        </Box>
      )}

      {drillDown && (
        <StrategyBreakdownModal
          open
          onClose={() => setDrillDown(null)}
          date={drillDown.date}
          simNumber={drillDown.simNumber}
          pnl={drillDown.pnl}
          percentile="stress"
          portfolio={drillDown.portfolio}
          strategyLabel={drillDown.scenarioLabel}
          source={drillDown.source}
        />
      )}
    </Box>
  );
}
